import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateFoodMoodSimpleDetailsMutation } from '../../hooks'
import { foodMoodPropType } from '../../prop-types'
import { setFoodMoodPageIsDirty } from '../../redux/actions'

import { foodMoodSimpleDetailsFormInitialValues } from './initial-values'

const FoodMoodSimpleDetailsForm = ({ foodMood, maxPosition }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.FOOD_MOOD.UPDATE_SIMPLE_DETAILS,
  ])

  const [
    updateFoodMoodSimpleDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateFoodMoodSimpleDetailsMutation()

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const initialValues = useMemo(
    () => foodMoodSimpleDetailsFormInitialValues(foodMood),
    [foodMood],
  )

  const handleSaveData = useCallback(
    data => updateFoodMoodSimpleDetails(foodMood.id, data),
    [foodMood.id, updateFoodMoodSimpleDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'FoodMood details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [
    initialValues.synonyms,
    initialValues.isExcludedFromSearch,
    initialValues.isExcludedFromBrowsing,
    initialValues.position,
    initialValues.description,
    resetForm,
  ])

  useEffect(() => {
    dispatch(setFoodMoodPageIsDirty({ isSimpleDetailsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editFoodMood"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{foodMood.name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="synonyms"
            label="Synonyms (separated by new line)"
            labelAbove
            placeholder="Input synonyms..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={12}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app browsing"
            name="isExcludedFromBrowsing"
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.FOOD_MOOD.UPDATE_SIMPLE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

FoodMoodSimpleDetailsForm.propTypes = {
  foodMood: foodMoodPropType.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

export default FoodMoodSimpleDetailsForm
